<!-- Haupt-Tab „Akkorde": Stimmung + Grundton + Akkordtyp wählen, Griffe auf Druck und Zug ansehen und anhören (nach Vorbild des Quetschnzirkels). Ohne Props. -->
<script setup lang="ts">
import {storeToRefs} from 'pinia'
import {computed} from 'vue'
import {useRoute, useRouter} from 'vue-router'

import type {ChordGripsRequest, Grip} from '@/components/harmonika/gripTypes'
import {BassLayoutEditor, instrumentTuning} from '@/components/harmonika'
import {playButtonTones} from '@/components/harmonika/buttonTone'
import InstrumentTuningSelect from '@/components/harmonika/InstrumentTuningSelect.vue'
import {useChordGrips} from '@/composables/useChordGrips'
import {useSettingsStore} from '@/stores/settings'

import ChordDirectionCard from './ChordDirectionCard.vue'
import {
  CHORD_TYPE_OPTIONS,
  chordOptionLabel,
  chordQueryPatch,
  type ChordSelection,
  type ChordTypeId,
  gripMidis,
  heardRootPc,
  parseChordQuery,
  ROOT_LABELS,
  rootName,
} from './chordFinder'

const ARPEGGIO_STEP_MS = 160
const CHORD_MS = 1400
const BASS_STEP_MS = 420

const settingsStore = useSettingsStore()
const {instrumentTuningId, instrumentTuningOffset, bassLayout, chordFinderShowBass} = storeToRefs(settingsStore)

// Die Auswahl steht in der URL (teilbar, überlebt Reload): gelesen aus der Route, geschrieben per
// `router.replace` mit NUR den eigenen Keys – nie `route.query` spreaden, die Datei-Keys ergänzt der
// Router-Guard (router/createAppRouter.ts). „Stimmung" wie beim Quetschnzirkel: die eigene Harmonika
// (ohne Transponieren, Default) ODER die Tonart des Lieds (mit Transponieren).
const route = useRoute()
const router = useRouter()

const selection = computed<ChordSelection>((oldValue) => {
  const next = parseChordQuery(route.query, instrumentTuningId.value)
  if (
      oldValue
      && oldValue.stimmungId === next.stimmungId
      && oldValue.rootPc === next.rootPc
      && oldValue.chordType === next.chordType
  ) return oldValue
  return next
})

function select(change: Partial<ChordSelection>) {
  const query = chordQueryPatch({...selection.value, ...change}, instrumentTuningId.value)
  void router.replace({name: 'chords', query})
}

const stimmungId = computed<string>({get: () => selection.value.stimmungId, set: (stimmungId) => select({stimmungId})})
const rootPc = computed<number>({get: () => selection.value.rootPc, set: (rootPc) => select({rootPc})})
const chordType = computed<ChordTypeId>({get: () => selection.value.chordType, set: (chordType) => select({chordType})})

const request = computed<ChordGripsRequest>((oldValue) => {
  const stimmungOffset = instrumentTuning(stimmungId.value).offset
  if (
      oldValue
      && oldValue.root_pc === rootPc.value
      && oldValue.chord_type === chordType.value
      && oldValue.stimmung_offset === stimmungOffset
      && oldValue.bass_layout === bassLayout.value
  ) return oldValue
  return {
    root_pc: rootPc.value,
    chord_type: chordType.value,
    stimmung_offset: stimmungOffset,
    bass_layout: bassLayout.value,
  }
})

const {response, loading, error} = useChordGrips(request)

const heardRoot = computed(() => (
    response.value ? rootName(heardRootPc(response.value.griff_root_pc, instrumentTuningOffset.value)) : '–'
))
const isTransposing = computed(() => stimmungId.value !== instrumentTuningId.value)

function playGrip(grip: Grip, arpeggio: boolean) {
  const midis = gripMidis(grip)
  if (!arpeggio) {
    playButtonTones(midis, CHORD_MS)
    return
  }
  midis.forEach((midi, index) => {
    playButtonTones([midi], CHORD_MS - index * ARPEGGIO_STEP_MS, index * ARPEGGIO_STEP_MS)
  })
}

function playBass(isDruck: boolean) {
  const slots = (isDruck ? response.value?.druck : response.value?.zug)?.bass_midi ?? []
  slots.forEach((midis, index) => {
    playButtonTones(midis, BASS_STEP_MS - 40, index * BASS_STEP_MS)
  })
}
</script>

<template>
  <div class="mx-auto flex w-full max-w-8xl flex-col gap-4">
    <form class="grid gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:grid-cols-3" @submit.prevent>
      <label class="flex flex-col gap-1 text-sm text-gray-700">
        Stimmung (Dur/Moll)
        <InstrumentTuningSelect v-model:tuning="stimmungId" with-minor class="border-gray-300 bg-white"/>
        <span class="text-xs text-gray-500">
          Deine Harmonika – oder die Tonart des Lieds, um zu transponieren.
        </span>
      </label>
      <label class="flex flex-col gap-1 text-sm text-gray-700">
        Grundton
        <select v-model.number="rootPc" class="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm">
          <option v-for="(label, pc) in ROOT_LABELS" :key="label" :value="pc">{{ label }}</option>
        </select>
      </label>
      <label class="flex flex-col gap-1 text-sm text-gray-700">
        Akkord
        <select v-model="chordType" class="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm">
          <option v-for="option in CHORD_TYPE_OPTIONS" :key="option.id" :value="option.id">
            {{ chordOptionLabel(rootPc, option) }}
          </option>
        </select>
      </label>
      <label class="flex items-center gap-2 text-sm text-gray-700 sm:col-span-3">
        <input v-model="chordFinderShowBass" type="checkbox" class="rounded border-gray-300">
        Bassknöpfe anzeigen
      </label>
    </form>

    <p v-if="error" class="rounded-md bg-red-50 p-3 text-sm text-red-700">{{ error }}</p>
    <p v-else-if="loading && !response" class="text-sm text-gray-500">Griffe werden gesucht …</p>

    <div v-if="response" class="grid gap-4 xl:grid-cols-2" :class="loading ? 'opacity-60' : ''">
      <ChordDirectionCard
          title="DRUCK"
          is-druck
          :result="response.druck"
          :music-xml="response.music_xml"
          :first-measure-index="0"
          :instrument-offset="instrumentTuningOffset"
          :bass-layout="chordFinderShowBass ? bassLayout : null"
          @play-grip="playGrip"
          @play-bass="playBass(true)"
      />
      <ChordDirectionCard
          title="ZUG"
          :is-druck="false"
          :result="response.zug"
          :music-xml="response.music_xml"
          :first-measure-index="response.druck.grips.length"
          :instrument-offset="instrumentTuningOffset"
          :bass-layout="chordFinderShowBass ? bassLayout : null"
          @play-grip="playGrip"
          @play-bass="playBass(false)"
      />
    </div>

    <!-- Dieselbe Belegung wie im Harmonika-Tab (Settings-Store): eine Änderung sucht die Bass-Begleitung neu
         und gilt auch für die Bass-Zeile der Konvertierung. -->
    <details class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <summary class="cursor-pointer text-sm font-semibold text-gray-700">Bass-Belegung anpassen</summary>
      <p class="mt-2 text-xs text-gray-500">
        Gilt überall – auch für die Bass-Zeile der Konvertierung und im Harmonika-Tab.
      </p>
      <div class="mt-3">
        <BassLayoutEditor/>
      </div>
    </details>

    <section class="flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700 shadow-sm">
      <h3 class="w-full font-semibold">Und so klingt es auf deiner Harmonika</h3>
      <label class="flex flex-col gap-1">
        Stimmung meiner Harmonika
        <InstrumentTuningSelect v-model:tuning="instrumentTuningId" class="border-gray-300 bg-white"/>
      </label>
      <div>
        Gehörter Grundton: <span class="font-mono text-base font-bold">{{ heardRoot }}</span>
        <span v-if="isTransposing" class="ml-2 text-xs text-gray-500">(transponiert)</span>
      </div>
    </section>
  </div>
</template>
